import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import MainLayout from 'layouts/MainLayout';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import MainLayoutProvider from 'providers/MainLayoutProvider';
import SimpleSignIn from 'pages/pages/authentication/simple/SignIn';
import FaceClock from 'pages/FaceClock';
import App from 'App';
import Error404 from 'pages/error/Error404';
import { useAuth } from 'hooks/useAuth'; // Import the custom hook
import { useNotificationRealtime } from 'hooks/useNotifications';
import LocationList from 'pages/apps/crm/LocationList';
import LocationDetails from 'pages/apps/crm/common/LocationDetails';
import SalesInvoice from 'pages/apps/crm/SalesInvoice';
import SalesInvoiceDetails from 'pages/apps/crm/common/SalesInvoiceDetails';
import SalesInvoiceForm from 'pages/apps/crm/SalesInvoiceForm';
import EditSalesInvoiceForm from 'pages/apps/crm/EditSalesInvoiceForm';
import ProcurementReturns from 'pages/apps/crm/ProcurementReturns';
import Gates from 'pages/gates/Gates';
import Vehicles from 'pages/Vehicles';
import CreateVehicle from 'pages/CreateVehicle';
import VehicleDetails from 'pages/VehicleDetails';
import VehicleServiceDetail from 'pages/VehicleServiceDetail';
/*import Showcase from 'pages/Showcase';
import DraggableExample from 'pages/modules/components/DraggableExample';
import Deals from 'pages/apps/crm/Deals';
import OffcanvasExample from 'pages/modules/components/OffcanvasExample';*/
import Kanban from 'pages/apps/kanban/Kanban';

import SupplierPayment from 'pages/apps/crm/SupplierPayment/SupplierPayment';
import Expenses from 'pages/apps/crm/Expenses/Expenses';
import SupplierPaymentDetails from 'pages/apps/crm/SupplierPayment/SupplierPaymentDetails';
import PurchaseOrder from 'pages/apps/crm/PurchaseOrder/PurchaseOrder';
import PurchaseOrderDetails from 'pages/apps/crm/PurchaseOrder/PurchaseOrderDetails';
import PendingPurchaseOrder from 'pages/apps/crm/PurchaseOrder/PendingPurchaseOrder';
import Resupply from 'pages/apps/crm/Resupply/Resupply';
import CustomerRequests from 'pages/apps/crm/Resupply/CustomerRequests';
import ProductSet from 'pages/apps/crm/ProductSet/ProductSet';
import ProductSetList from 'pages/apps/crm/ProductSet/ProductSetList';
import CompanyCustomerList from 'pages/apps/crm/Customer/CompanyCustomerList';
import CompanyDetails from 'pages/apps/crm/Customer/CompanyDetails';
import IndividualCustomerList from 'pages/apps/crm/Customer/IndividualCustomerList';
import IndividualCustomerDetails from 'pages/apps/crm/Customer/IndividualCustomerDetails';
import Discount from 'pages/apps/crm/Discount/Discount';
import DiscountDetails from 'pages/apps/crm/Discount/DiscountDetails';
import ResearchChat from 'pages/ResearchChat';
import Marketing from 'pages/Marketing';
import VehicleImageCategories from 'pages/VehicleImageCategories';
import { FeatureRoles } from 'utils/roleAccess';
const CarMakes = React.lazy(() => import('pages/CarMakes'));
const Services = React.lazy(() => import('pages/Services'));
const queryClient = new QueryClient();

const BranchProductInventory = React.lazy(
  () => import(`pages/apps/crm/BranchProductInventory/BranchProductInventory`)
);

const BranchList = React.lazy(() => import(`pages/apps/crm/BranchList`));
const TransferRequests = React.lazy(
  () => import(`pages/apps/crm/TransferRequests/TransferRequests`)
);
const TransferRequestDetails = React.lazy(
  () => import(`pages/apps/crm/TransferRequests/TransferRequestDetails`)
);

const BranchDetails = React.lazy(
  () => import(`pages/apps/crm/common/BranchDetails`)
);
const UserList = React.lazy(() => import(`pages/apps/crm/UserList`));
const Timelog = React.lazy(() => import(`pages/apps/crm/Timelog/Timelog`));
const HrManage = React.lazy(
  () => import(`pages/apps/crm/HumanResources/HrManage`)
);
const EmployeeList = React.lazy(
  () => import(`pages/apps/crm/HumanResources/EmployeeList`)
);
const FaceEnrollment = React.lazy(
  () => import(`pages/apps/crm/HumanResources/FaceEnrollment`)
);
const HolidayList = React.lazy(
  () => import(`pages/apps/crm/HumanResources/HolidayList`)
);
const LeaveList = React.lazy(
  () => import(`pages/apps/crm/HumanResources/LeaveList`)
);
const CashAdvanceList = React.lazy(
  () => import(`pages/apps/crm/HumanResources/CashAdvanceList`)
);
const LoanList = React.lazy(
  () => import(`pages/apps/crm/HumanResources/LoanList`)
);
const OvertimeRequestList = React.lazy(
  () => import(`pages/apps/crm/HumanResources/OvertimeRequestList`)
);
const PresentToday = React.lazy(
  () => import(`pages/apps/crm/HumanResources/PresentToday`)
);
const SalaryOverview = React.lazy(
  () => import(`pages/apps/crm/HumanResources/SalaryOverview`)
);
const ThirteenthMonth = React.lazy(
  () => import(`pages/apps/crm/HumanResources/ThirteenthMonth`)
);
const ActivityLog = React.lazy(
  () => import(`pages/apps/crm/ActivityLog/ActivityLog`)
);
const AiCallLogs = React.lazy(
  () => import(`pages/apps/crm/AiCallLogs/AiCallLogs`)
);
const ProductSearchLogs = React.lazy(
  () => import(`pages/apps/crm/ProductSearchLogs/ProductSearchLogs`)
);
const SupplierList = React.lazy(() => import(`pages/apps/crm/SupplierList`));
const SupplierDetails = React.lazy(
  () => import(`pages/apps/crm/common/SupplierDetails`)
);
const ProductBrandList = React.lazy(
  () => import(`pages/apps/crm/ProductBrandList`)
);
const ProductCategoryList = React.lazy(
  () => import(`pages/apps/crm/ProductCategoryList`)
);
const ProductList = React.lazy(() => import(`pages/apps/crm/ProductList`));
const ProductDetails = React.lazy(
  () => import(`pages/apps/crm/common/ProductDetails`)
);
const BranchProductDetails = React.lazy(
  () => import(`pages/apps/crm/BranchProductInventory/BranchProductDetails`)
);
const ForApproval = React.lazy(
  () => import(`pages/apps/crm/BranchProductInventory/ForApproval`)
);
const ProcurementInvoice = React.lazy(
  () => import(`pages/apps/crm/ProcurementInvoice`)
);
const ProcurementDetails = React.lazy(
  () => import(`pages/apps/crm/common/ProcurementDetails`)
);
const ProcurementSummary = React.lazy(
  () => import(`pages/apps/crm/ProcurementSummary`)
);
const SalesReport = React.lazy(
  () => import(`pages/apps/crm/SalesReport/SalesReport`)
);
const RecentlyApprovedPackagePhotos = React.lazy(
  () => import(`pages/apps/crm/PackagePhotos/RecentlyApprovedPackagePhotos`)
);
const VehicleRepairReport = React.lazy(
  () => import(`pages/apps/crm/VehicleRepairReport/VehicleRepairReport`)
);

const Notifications = React.lazy(
  () => import(`pages/notifications/Notifications`)
);

const ProductsFilter = React.lazy(
  () => import(`pages/apps/e-commerce/customer/ProductDetails`)
);

const BarcodeLookup = React.lazy(
  () => import(`pages/apps/crm/common/BarcodeLookup`)
);
const ActualQuantity = React.lazy(
  () => import(`pages/apps/crm/ActualQuantity/ActualQuantity`)
);
const TaskBoard = React.lazy(
  () => import(`pages/apps/crm/Tasks/TaskBoard`)
);

const ProtectedRoute = ({
  children,
  requiredRole
}: {
  children: JSX.Element;
  requiredRole?: string[];
}) => {
  const { token } = useAuth();

  const hasRequiredRole = requiredRole
    ? token?.roles?.some(role => requiredRole.includes(role.item2 as string))
    : true;

  return token && hasRequiredRole ? children : <Navigate to="/404" replace />;
};

const SpinnerLoader = () => {
  return (
    <>
      <Spinner animation="border" role="status" className="me-2">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </>
  );
};

const NotificationRealtimeBridge = () => {
  useNotificationRealtime();
  return null;
};

const StaffLayout = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <NotificationRealtimeBridge />
      <MainLayoutProvider>
        <MainLayout />
      </MainLayoutProvider>
    </QueryClientProvider>
  );
};

const HomePage = () => {
  return (
    <ProtectedRoute>
      <React.Suspense fallback={<SpinnerLoader />}>
        <BranchProductInventory />
      </React.Suspense>
    </ProtectedRoute>
  );
};

const routes: RouteObject[] = [
  {
    element: <App />,
    children: [
      {
        path: '/sign-in',
        element: <SimpleSignIn />
      },
      {
        path: '/face-clock',
        element: <FaceClock />
      },
      {
        path: '/',
        element: <StaffLayout />,
        children: [
          {
            path: 'test/',
            element: <Kanban></Kanban>
          },
          {
            index: true,
            element: <HomePage />
          },
          {
            path: 'barcode-lookup/',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.inventorySearch]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <BarcodeLookup />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'actual-quantity/:branchDomain',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.actualQuantity]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ActualQuantity />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'company-list',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.customers]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CompanyCustomerList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'company-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.customers]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CompanyDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'customer-list',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.customers]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <IndividualCustomerList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'customer-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.customers]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <IndividualCustomerDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'discount',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Discount />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'discount-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <DiscountDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: `product/:branchDomain/`,
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.inventorySearch]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <BranchProductInventory />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product/:branchDomain/for-approval',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.inventorySearch]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ForApproval />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product/:branchDomain/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.inventorySearch]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <BranchProductDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-set',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductSet />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-set-search',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductSetList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'purchase-order',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.purchaseOrder]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <PurchaseOrder />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'purchase-order/pending',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.purchaseOrder]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <PendingPurchaseOrder />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'resupply',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.resupply]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Resupply />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'customer-requests',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.resupply]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CustomerRequests />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'purchase-order-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.purchaseOrder]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <PurchaseOrderDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'transfer-requests',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <TransferRequests />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'transfer-requests/transfer-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <TransferRequestDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'branch-details',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <BranchList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'branch-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <BranchDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'locations',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.inventorySearch]}>
                <LocationList />
              </ProtectedRoute>
            )
          },
          {
            path: 'location-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <LocationDetails />
              </ProtectedRoute>
            )
          },
          {
            path: 'procurement-invoices',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.procurementInvoice]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProcurementInvoice />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'procurement-invoices/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.procurementInvoice]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProcurementDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'procurement-returns',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.returns]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProcurementReturns />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'procurement-summary',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.procurementSummary]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProcurementSummary />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'store-report',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.salesReport]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SalesReport />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'sales-report',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.salesReport]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SalesReport />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'shop-report',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicleRepairReport]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <VehicleRepairReport />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'vehicle-repair-report',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicleRepairReport]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <VehicleRepairReport />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-list',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.products]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-list/product-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.products]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-brand-list',
            element: (
              <ProtectedRoute
                requiredRole={[...FeatureRoles.productCategoriesBrands]}
              >
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductBrandList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-category-list',
            element: (
              <ProtectedRoute
                requiredRole={[...FeatureRoles.productCategoriesBrands]}
              >
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductCategoryList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'sales-invoice',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.salesInvoice]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SalesInvoice />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'new-package-photos',
            element: (
              <ProtectedRoute
                requiredRole={[...FeatureRoles.recentlyApprovedPackagePhotos]}
              >
                <React.Suspense fallback={<SpinnerLoader />}>
                  <RecentlyApprovedPackagePhotos />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'sales-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.salesInvoice]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SalesInvoiceDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'new-sale-transaction',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.newSaleTransaction]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SalesInvoiceForm />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'edit-sale-transaction/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.newSaleTransaction]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <EditSalesInvoiceForm />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'supplier-list',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SupplierList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'supplier-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.supplierDetails]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SupplierDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'expenses',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.expenses]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Expenses />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'supplier-payments',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.supplierPayments]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SupplierPayment />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'supplier-payment-details/:id',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.supplierPayments]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SupplierPaymentDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'user-list',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <UserList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'timelog',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.timelog]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Timelog />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/employees',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <EmployeeList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/employees/:userId/face',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <FaceEnrollment />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/manage',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <HrManage />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/holidays',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <HolidayList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/leaves',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <LeaveList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/cash-advances',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CashAdvanceList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/loans',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <LoanList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/overtime-requests',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <OvertimeRequestList />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/present-today',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <PresentToday />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/salary-overview',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.humanResources]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <SalaryOverview />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'hr/thirteenth-month',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.thirteenthMonth]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ThirteenthMonth />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'gates',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.gate]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Gates />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'activity-log',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.activityLog]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ActivityLog />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'notifications',
            element: (
              <ProtectedRoute>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Notifications />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'ai-call-logs',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.aiCallLogs]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <AiCallLogs />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'product-search-logs',
            element: (
              <ProtectedRoute
                requiredRole={[...FeatureRoles.productSearchLogs]}
              >
                <React.Suspense fallback={<SpinnerLoader />}>
                  <ProductSearchLogs />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'services',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Services />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'car-makes',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CarMakes />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'car-makes/:makeKey',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CarMakes />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'car-makes/:makeKey/:modelKey',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CarMakes />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'car-makes/:makeKey/:modelKey/:year',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CarMakes />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'vehicles',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <Vehicles />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'vehicles/create',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <CreateVehicle />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'vehicles/:vehicleId',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <VehicleDetails />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'vehicle-service/:vehicleServiceId',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.vehicles]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <VehicleServiceDetail />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'ai-assistant',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.aiQuery]}>
                <ResearchChat />
              </ProtectedRoute>
            )
          },
          {
            path: 'research',
            element: <Navigate to="/ai-assistant" replace />
          },
          {
            path: 'tasks',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.tasks]}>
                <React.Suspense fallback={<SpinnerLoader />}>
                  <TaskBoard />
                </React.Suspense>
              </ProtectedRoute>
            )
          },
          {
            path: 'marketing',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <Marketing />
              </ProtectedRoute>
            )
          },
          {
            path: 'vehicle-image-categories',
            element: (
              <ProtectedRoute requiredRole={[...FeatureRoles.adminOnly]}>
                <VehicleImageCategories />
              </ProtectedRoute>
            )
          },
          {
            path: 'ecomm',
            element: <ProductsFilter />
          }
        ]
      },
      {
        path: '404',
        element: <Error404 />
      },
      {
        path: '*',
        element: <Error404 />
      }
    ]
  }
];

export const router = createBrowserRouter(routes);

export default routes;
